import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { createContext, useContext, useState } from 'react';
import toast from 'react-hot-toast';
import config from '../config';
import accessories from '../data/accessories';
import calculateClaimableTreats from '../utils/calculateClaimableTreats';
import claimTreatsApi from '../utils/claimTreats';
import getErrorMessage from '../utils/getErrorMessage';
import moveInKitties from '../utils/moveInKitties';
import { useAlgo } from './useAlgo';
import { useAllKitties } from './useAllKitties';
import { useCitizensQuery, useRenterDetailsQuery } from './useCitizensQuery';
import { useCollabAssets } from './useCollabAssets';
import { useKittiesStore } from './useKittiesStore';
import { useMyCities } from './useMyCities';

const CitizensContext = createContext(null);
export const useCitizens = () => useContext(CitizensContext);

export const CitizensProvider = ({ children, city }) => {
  const queryClient = useQueryClient();
  const { optInForMoveIn, optInForMoveOut, optInForClaimTreats } = useAlgo();
  const { allKitties } = useAllKitties();
  const { myKitties, myAccessories } = useKittiesStore();
  const { myCollabAssets } = useCollabAssets(city);
  const [selectedKitties, setSelectedKitties] = useState([]);
  const { allCities, myCities } = useMyCities();
  const [loading, setLoading] = useState(false);
  const [claimTreatsLoading, setClaimTreatsLoading] = useState(false);

  const renterDetailsQuery = useRenterDetailsQuery({ city });
  const citizenDetailsQuery = useCitizensQuery({ city });

  const transformCitizen = (citizen) => {
    const accessory = accessories.find((a) => a.id === citizen.kitty);
    const kitty = allKitties.find((k) => k.id === citizen.kitty);
    const collab = myCollabAssets.find((a) => a['asset-id'] === citizen.kitty);
    const isAccessory = !!accessory;
    const isCollab = !kitty && !isAccessory;
    const claimableTreats = calculateClaimableTreats({ ...(collab || kitty), isAccessory }, city, citizen.lastClaimedDate.toDate());

    return {
      citizenDetails: {
        ...citizen,
        moveInDate: citizen.moveInDate.toDate(),
        lastClaimedDate: citizen.lastClaimedDate.toDate(),
      },
      ...kitty,
      ...collab,
      ...accessory,
      claimableTreats,
      isCollab,
      isAccessory,
      isRenter: citizen.isRenter,
      collection: Object.values(config.collabBlocks).find((c) => c.assets.flat().includes(citizen.kitty))?.collection,
    };
  };

  const citizens = (citizenDetailsQuery.data ?? []).map(transformCitizen);
  const renters = (renterDetailsQuery.data ?? []).map(transformCitizen);

  const rentersInCities = allCities.map((c) => c.renters ?? []).flat();
  const kittiesLivingInCities = allCities.map((c) => c.citizens ?? []).flat();
  const rentersInMyCities = myCities.map((c) => c.renters ?? []).flat();
  const kittiesLivingInMyCities = myCities.map((c) => c.citizens ?? []).flat();
  const kittiesWithoutHome = [...myKitties, ...myCollabAssets, ...myAccessories].filter(
    (kitty) =>
      !kittiesLivingInCities.includes(kitty.id || kitty['asset-id']) &&
      !kittiesLivingInMyCities.includes(kitty.id || kitty['asset-id']) &&
      !rentersInCities.includes(kitty.id || kitty['asset-id']) &&
      !rentersInMyCities.includes(kitty.id || kitty['asset-id'])
  );

  const selectKitty = (id) => {
    const tooManyKitties = city.currentPopulation + selectedKitties.length + 1 > city.maxPopulation;

    if (selectedKitties.includes(id)) {
      setSelectedKitties(selectedKitties.filter((kitty) => kitty !== id));
      return;
    }

    if (tooManyKitties) {
      return toast.error(`Only ${city.maxPopulation} Shitty Kitties can move to this Shitty City.`);
    }

    setSelectedKitties([...selectedKitties, id]);
  };

  const selectAllKitties = () => {
    const tooManyKitties = city.currentPopulation + kittiesWithoutHome.length > city.maxPopulation;

    if (tooManyKitties) {
      return toast.error(`Only ${city.maxPopulation} Shitty Kitties can move to this Shitty City.`);
    }

    if (selectedKitties.length === kittiesWithoutHome.length) return setSelectedKitties([]);
    setSelectedKitties(kittiesWithoutHome.map((kitty) => kitty.id || kitty['asset-id']));
  };

  const addToCity = async (isRenter = false) => {
    try {
      setLoading(true);
      const txn = await optInForMoveIn(city.index, selectedKitties, isRenter);
      await moveInKitties(txn);
      if (selectedKitties.length > 1) toast.success(`${selectedKitties.length} Shitizens have successfully moved in!`);
      if (selectedKitties.length === 1) toast.success('1 Shitizen has successfully moved in!');
      if (selectedKitties.length === 0) toast.success(`The Shitizens have successfully moved out!`);
      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error(getErrorMessage(error));
    } finally {
      setSelectedKitties([]);
      setLoading(false);
    }
  };

  const removeFromCity = async (assetId, isRenter) => {
    try {
      const txn = await optInForMoveOut(city.index, assetId, isRenter);
      await axios.post(
        '/api/cities/move-out',
        {
          txn,
        },
        {
          headers: {
            Authorization: process.env.NEXT_PUBLIC_API_KEY,
          },
        }
      );
      queryClient.invalidateQueries({ queryKey: ['citizenDetails', city.id] });
      queryClient.invalidateQueries({ queryKey: ['renterDetails', city.id] });
      queryClient.invalidateQueries({ queryKey: ['allCities'] });
      toast.success('Shitizens successfully moved out!');
    } catch (error) {
      console.error(error);
      toast.error(getErrorMessage(error));
    }
  };

  const claimTreats = async () => {
    try {
      setClaimTreatsLoading(true);
      const txn = await optInForClaimTreats(city.index);
      await claimTreatsApi(txn);
      toast.success('Treats successfully claimed!');
      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error(getErrorMessage(error));
    } finally {
      setClaimTreatsLoading(false);
    }
  };

  return (
    <CitizensContext.Provider
      value={{
        addToCity,
        removeFromCity,
        claimTreats,
        selectedKitties,
        selectAllKitties,
        selectKitty,
        loading,
        claimTreatsLoading,
        kittiesWithoutHome,
        citizens,
        renters,
      }}
    >
      {children}
    </CitizensContext.Provider>
  );
};
